import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'
import classnames from 'classnames'
import withStyles from 'react-jss'

import { CheckboxField, TextField, ReCaptchaField } from '@elo-kit/components'
import { validateEmailUniqueness } from 'utils/validators-auth.utils'
import { validators } from 'utils/validators.utils'
import { marketingWebHosts } from 'libs/configs'

import embeddableItemStyles from '../../embeddableItemStyles'

const PoweredBy = withStyles(embeddableItemStyles)(({ classes }) => (
  <div className={classes.poweredBy}>
    {I18n.t('react.shared.powered_by')}
    <a className={classes.elopageLink} href={`https://${marketingWebHosts.production}/`}>
      ablefy
    </a>
  </div>
))

@inject('affiliateProgramsStore', 'sellerStore')
@observer
class RegistrationForm extends Component {
  @observable emailExists = false

  constructor(props) {
    super(props)
    makeObservable(this)

    this.recaptchaRef = React.createRef()
  }

  componentDidMount() {
    const {
      userData: { email },
    } = this.props

    if (validators.isEmail(email)) {
      this.validateEmail()
    }
  }

  @computed get isFormValid() {
    const {
      userData: { firstName, lastName, password, termsAccepted, email },
    } = this.props

    return email && firstName && lastName && password && termsAccepted
  }

  @action validateEmail = async () => {
    const {
      userData: { email },
    } = this.props

    if (validators.isEmail(email)) {
      this.emailExists = !!(await validateEmailUniqueness(email))
    }
  }

  handleFormSubmit = () => {
    this.recaptchaRef.current.executeAsync().then((recaptcha) => {
      const { handleSubmit, setRecaptcha } = this.props
      setRecaptcha(recaptcha)
      handleSubmit()
    })
  }

  render() {
    const {
      classes,
      labelsEnabled,
      embedPrefs,
      locale,
      handleUserData,
      handleCloseForm,
      sellerStore,
      userData: { firstName, lastName, password, termsAccepted, email },
      setRecaptcha,
    } = this.props

    const emailErrorMsg = this.emailExists
      ? I18n.t('front.widgets.embed_form.email_exists', { locale })
      : I18n.t('front.widgets.embed_form.invalid_email', { locale })
    const termsLabel = (
      <span dangerouslySetInnerHTML={{ __html: I18n.t('front.widgets.embed_form.accept_terms_text', { locale }) }} />
    )
    const emailValid = validators.isEmail(email) && !this.emailExists
    const isFormValid = email && emailValid && firstName && lastName && password && termsAccepted
    const showPoweredBy = !sellerStore.isAppActive('elopage-badge')

    return (
      <div className={classes.registrationFormContainer}>
        <i
          className='fas fa-times-circle'
          onClick={() => {
            handleCloseForm()
            setRecaptcha(null)
          }}
        />
        <div className='fields-container'>
          <TextField
            label={labelsEnabled && I18n.t('front.widgets.embed_form.first_name', { locale })}
            placeholder={I18n.t('front.widgets.embed_form.first_name', { locale })}
            value={firstName}
            onChange={(value) => handleUserData('firstName', value)}
            className='fields-container__grow-2'
            labelClassName={classes.embedFormLabel}
            required
          />
          <TextField
            label={labelsEnabled && I18n.t('front.widgets.embed_form.last_name', { locale })}
            placeholder={I18n.t('front.widgets.embed_form.last_name', { locale })}
            value={lastName}
            onChange={(value) => handleUserData('lastName', value)}
            labelClassName={classes.embedFormLabel}
            className='fields-container__grow-2'
            required
          />
        </div>
        <TextField
          label={labelsEnabled && I18n.t('front.widgets.embed_form.email', { locale })}
          placeholder={I18n.t('front.widgets.embed_form.email', { locale })}
          value={email}
          onChange={(value) => handleUserData('email', value)}
          labelClassName={classes.embedFormLabel}
          isInvalid={emailValid !== undefined && !emailValid}
          errorMessage={emailErrorMsg}
          onBlur={this.validateEmail}
          required
        />
        <TextField
          label={labelsEnabled && I18n.t('front.widgets.embed_form.password', { locale })}
          placeholder={I18n.t('front.widgets.embed_form.password', { locale })}
          value={password}
          onChange={(value) => handleUserData('password', value)}
          labelClassName={classes.embedFormLabel}
          type='password'
          required
        />
        <div className='field'>
          <ReCaptchaField recaptchaRef={this.recaptchaRef} locale={locale} />
        </div>
        <CheckboxField
          label={termsLabel}
          checked={termsAccepted}
          onChange={() => handleUserData('termsAccepted', !termsAccepted)}
          className='checkbox-field--no-margin'
        />
        <div className={classes.buttonContainer}>
          <button
            onClick={this.handleFormSubmit}
            className={classnames(classes.button, classes[embedPrefs.buttonAnimation])}
            disabled={!isFormValid}
          >
            {I18n.t('front.widgets.embed_form.create_account', { locale })}
          </button>
        </div>
        {showPoweredBy && <PoweredBy />}
      </div>
    )
  }
}

export default withStyles(embeddableItemStyles)(RegistrationForm)
